import { z } from 'zod';
import { DateOnlySchema, IdSchema, PageInfoSchema, TimestampSchema } from './common.js';
import { ParkIdFilterSchema, ParkKindSchema } from './parks.js';
export const DashboardSummarySchema = z.strictObject({
    companies: z.number().int().nonnegative(),
    openLeads: z.number().int().nonnegative(),
    openOpportunities: z.number().int().nonnegative(),
    activeProjects: z.number().int().nonnegative(),
    overdueTasks: z.number().int().nonnegative(),
    unreadNotifications: z.number().int().nonnegative(),
});
/**
 * The six template CRM entities plus Suncoast's own three.
 *
 * The template values are kept because report_export rows may already reference
 * them and a PostgreSQL enum value cannot be dropped without recreating the
 * type. They export empty for this client, which is honest: the tables exist and
 * hold nothing.
 */
export const ReportTypeSchema = z.enum([
    'companies',
    'contacts',
    'leads',
    'opportunities',
    'projects',
    'tasks',
    'reservations',
    'leases',
    'parks',
]);
export const ReportExportStatusSchema = z.enum([
    'pending',
    'processing',
    'completed',
    'failed',
    'expired',
]);
export const CreateReportExportRequestSchema = z.strictObject({
    reportType: ReportTypeSchema,
    fromDate: DateOnlySchema.nullable().default(null),
    toDate: DateOnlySchema.nullable().default(null),
});
export const ReportExportSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    requestedByMembershipId: IdSchema,
    reportType: ReportTypeSchema,
    status: ReportExportStatusSchema,
    fromDate: DateOnlySchema.nullable(),
    toDate: DateOnlySchema.nullable(),
    fileName: z.string().nullable(),
    failureCode: z.string().nullable(),
    expiresAt: TimestampSchema.nullable(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const ReportExportListSchema = z.strictObject({
    items: z.array(ReportExportSchema),
    page: PageInfoSchema,
});
export const ReportExportDownloadSchema = z.strictObject({
    url: z.url(),
    expiresAt: TimestampSchema,
});
// ---------------------------------------------------------------------------
// Deliverable 8 — reconciliation status
// ---------------------------------------------------------------------------
/**
 * A month, as YYYY-MM.
 *
 * Not a DateOnly: "September 2026" is not a day, and letting a day through would
 * make 2026-09-01 and 2026-09-14 look like different months to a caller while the
 * database stores both as September.
 */
export const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');
export const ReconciliationStateSchema = z.enum(['open', 'reconciled']);
/**
 * A park's occupancy figures for one month, as they stood when it was signed off.
 *
 * Counts only; every rate on the page is derived from these, so a locked month
 * and a live one are rendered by the same arithmetic.
 *
 * A plain object rather than the occupancy cell itself, and not strict: a
 * snapshot is written once and read for years, while the live cell gains fields
 * whenever the dashboard does. Tying the two together would make every new
 * dashboard field a reason a signed-off month stops loading. A figure added
 * later arrives here optional, because older snapshots never held it.
 */
export const LockedOccupancySchema = z.object({
    sites: z.number().int().nonnegative(),
    /** Full-time: sites under a lease. */
    occupied: z.number().int().nonnegative(),
    /** Short-term lots booked 15+ nights, and the lots they were drawn from. */
    shortTermOccupied: z.number().int().nonnegative(),
    shortTermSites: z.number().int().nonnegative(),
    /** Whether the park sells short stays at all. */
    takesShortTerm: z.boolean(),
    moveIns: z.number().int().nonnegative(),
    moveOuts: z.number().int().nonnegative(),
    moveInGoal: z.number().int().nonnegative().nullable(),
    /** Added 2026-10-01; months frozen before then were all monthly goals. */
    moveInGoalMonths: z.number().int().min(1).max(12).default(1),
});
/**
 * What locking a month froze.
 *
 * Stephanie Friedl, 2026-09-24: once a month has been reviewed in the monthly
 * meeting it should not change underneath her -- "that way, we don't even change
 * the history that we reviewed." Every figure the portal shows is otherwise
 * recomputed from live tables on each request, so a lease corrected in Rent
 * Manager in September quietly rewrites July.
 */
export const ReconciliationSnapshotSchema = z.object({
    capturedAt: TimestampSchema,
    occupancy: LockedOccupancySchema,
});
export const ReconciliationStatusSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    month: MonthSchema,
    status: ReconciliationStateSchema,
    /** Null whenever status is open, including after a month has been reopened. */
    reconciledAt: TimestampSchema.nullable(),
    /** Display name of whoever signed it off, or null. Never an email. */
    reconciledBy: z.string().nullable(),
    /**
     * The figures frozen at the last sign-off. Kept when the month is reopened, so
     * what was published can still be read; whether it is still the month's
     * answer is `status`. Null for a month never signed off, or one signed off
     * before months were frozen (2026-09-29).
     */
    snapshot: ReconciliationSnapshotSchema.nullable(),
});
export const ReconciliationQuerySchema = z.strictObject({
    from: MonthSchema,
    to: MonthSchema,
    parkId: ParkIdFilterSchema,
});
export const ReconciliationListResponseSchema = z.strictObject({
    from: MonthSchema,
    to: MonthSchema,
    /**
     * Every park-month in the requested range, including those nobody has touched.
     *
     * A month with no stored row is returned as `open` rather than omitted. The
     * alternative makes every caller reconstruct the difference between "not
     * reconciled" and "not returned", and a grid with holes in it reads as missing
     * data rather than as work still outstanding.
     */
    items: z.array(ReconciliationStatusSchema),
});
export const SetReconciliationRequestSchema = z.strictObject({
    parkId: IdSchema,
    month: MonthSchema,
    status: ReconciliationStateSchema,
});
/**
 * Lock or reopen a whole month: every park, together.
 *
 * Her ask on 2026-09-24 was "a button that's like, this month is locked" -- a
 * month, not five park-months. One request, so the month is never left half
 * locked because the third of five calls failed.
 */
export const SetMonthReconciliationRequestSchema = z.strictObject({
    month: MonthSchema,
    status: ReconciliationStateSchema,
});
// ---------------------------------------------------------------------------
// The portfolio report: Stephanie Friedl's monthly meeting page (2026-09-24)
// ---------------------------------------------------------------------------
/** A month, `2026-08`, or a quarter, `2026-Q3`. */
export const PortfolioPeriodSchema = z
    .string()
    .regex(/^\d{4}-((0[1-9]|1[0-2])|Q[1-4])$/, 'Expected YYYY-MM or YYYY-Qn');
export const PortfolioQuerySchema = z.strictObject({ period: PortfolioPeriodSchema });
/**
 * One park's period, or the whole portfolio's. Occupancy is where the period
 * ended; moves and the goal add up across its months.
 */
export const PortfolioFiguresSchema = z.strictObject({
    sites: z.number().int().nonnegative(),
    /** Full-time: sites under a lease. */
    occupied: z.number().int().nonnegative(),
    shortTermOccupied: z.number().int().nonnegative(),
    shortTermSites: z.number().int().nonnegative(),
    takesShortTerm: z.boolean(),
    shortTermPercent: z.number().int().min(0).nullable(),
    /** Full-time plus short-term at 15+ nights: occupancy as the client defines it. */
    totalOccupied: z.number().int().nonnegative(),
    totalPercent: z.number().int().min(0).nullable(),
    moveIns: z.number().int().nonnegative(),
    moveOuts: z.number().int().nonnegative(),
    netMoveIns: z.number().int(),
    /** The park's goal and the months it covers, as she set them. */
    moveInGoal: z.number().int().nonnegative().nullable(),
    moveInGoalMonths: z.number().int().min(1).max(12),
    /**
     * The goal is judged over its own cadence, never less than the period: a
     * monthly goal over the month (or quarter), Holiday Village's "1 every other
     * month" over the last two months, Lake Letta MHC's "1 for the year" over the
     * last twelve. These are the months, the moves inside them, and the target.
     * The target can be fractional for a portfolio total or a quarter of a
     * two-month goal (1.5). Null when there is no goal.
     */
    goalWindowMonths: z.number().int().min(1).max(12),
    goalWindowMoveIns: z.number().int().nonnegative(),
    goalWindowNetMoveIns: z.number().int(),
    goalTarget: z.number().nonnegative().nullable(),
});
/**
 * Which figures a park shows. `locked`: frozen at sign-off. `live`: not signed
 * off. `unfrozen`: signed off before months were frozen, so shown live. `mixed`:
 * a quarter whose months differ.
 */
export const PortfolioSourceSchema = z.enum(['locked', 'live', 'unfrozen', 'mixed']);
/**
 * A park's budget for the period, from Rent Manager's Fiscal Year Budget, summed
 * over the period's months. `income`, `expense` and `noi` are the report's own
 * Total Income, Total Expense and NOI lines; `netIncome` comes after the
 * non-operating lines (interest), and is the figure Stephanie Friedl's own
 * dashboard shows as its NOI budget.
 */
export const PortfolioBudgetSchema = z.strictObject({
    income: z.number(),
    expense: z.number(),
    noi: z.number(),
    nonOperating: z.number(),
    netIncome: z.number(),
});
/**
 * What a goal measures. Gross and net move-ins share the park's move-in goal, as
 * on her own dashboard ("vs the same goal"); revenue and NOI are judged against
 * the budget; full-time residents is a stored goal with a deadline (Sebring
 * Grove's 90 by 31 May 2027).
 */
export const PortfolioGoalKeySchema = z.enum([
    'grossMoveIns',
    'netMoveIns',
    'fullTimeResidents',
    'revenue',
    'noi',
]);
/**
 * One goal, judged on progress through its whole window rather than the month.
 *
 * Stephanie Friedl, screen recording 2026-10-20, 01:08: "maybe we met the goals
 * for the month, but the goals for the month don't matter if we only hit one out
 * of four. So it needs to be based off our annual progress toward our annual
 * goal." And 01:56, the arithmetic: the annual goal divided by twelve, times the
 * months gone.
 *
 * Every goal has the same shape so every bar is read the same way: it runs from
 * `baseline` to `target` across `months`, and by the end of the period it should
 * have reached `expected`. A flow (move-ins, revenue) starts at zero; a level
 * (residents) starts where it stood when the goal was set.
 */
/**
 * Stephanie Friedl's goal bands (2026-10-02) as the lowest percentage of goal
 * each starts at; below `behind` is off track. One copy for the colours and for
 * the API's off-budget lines.
 */
export const GOAL_BAND_FLOORS = { ahead: 100, onTrack: 96, close: 90, behind: 80 };
export const PortfolioGoalSchema = z.strictObject({
    key: PortfolioGoalKeySchema,
    /** The goal's window: the park's fiscal year, or a stored goal's own months. */
    firstMonth: MonthSchema,
    lastMonth: MonthSchema,
    months: z.number().int().positive(),
    /** Months of the window gone by the end of the period; part of one for a month in progress. */
    monthsElapsed: z.number().nonnegative(),
    baseline: z.number(),
    target: z.number(),
    /** Where it stood at the end of the period. Null when a month so far has no figure. */
    actual: z.number().nullable(),
    /**
     * Where it should stand by then: the target's share of the months gone, or for
     * money the budget to date, which is the same arithmetic with the budget's own
     * seasons in it instead of twelve equal slices.
     */
    expected: z.number().nullable(),
    /**
     * Progress against `expected`, rounded: the figure the colour bands judge, so
     * the colour never disagrees with the number shown. Null when there is
     * nothing yet to judge -- the window has not started, or nothing was due.
     */
    pacePercent: z.number().int().nullable(),
    /** Progress against the whole goal, rounded and uncapped: how full the bar is. */
    progressPercent: z.number().int().nullable(),
    /** Who set a stored goal and where; null for one derived from cadence or budget. */
    source: z.string().nullable(),
});
export const PortfolioParkSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    /** An entity (Suncoast Communities itself) has finances and no occupancy. */
    kind: ParkKindSchema,
    propertyType: z.string().nullable(),
    /** Null when a month of the period has no figures at all. */
    figures: PortfolioFiguresSchema.nullable(),
    /** The park's goals, judged through the end of the period. Empty for an entity with no budget. */
    goals: z.array(PortfolioGoalSchema),
    /** Null when any month of the period has no budget loaded. */
    budget: PortfolioBudgetSchema.nullable(),
    /** The same figures from Rent Manager's actuals; null until every month has them. */
    actual: PortfolioBudgetSchema.nullable(),
    source: PortfolioSourceSchema,
    lockedMonths: z.number().int().nonnegative(),
    /**
     * The period's months that are signed off, frozen or not. A signed-off month's
     * notes are frozen with it, so this is what decides whether a note can be
     * written there.
     */
    reconciledMonths: z.array(MonthSchema),
    /**
     * The period's months whose move-ins and move-outs are still Rent Manager's,
     * not yet confirmed and closed by the park (Stephanie Friedl, 2026-10-23,
     * Spark 287230). Empty for an entity, which has no moves.
     */
    unconfirmedMoveMonths: z.array(MonthSchema),
    /** The latest sign-off in the period, and who made it. Display name only. */
    lockedAt: TimestampSchema.nullable(),
    lockedBy: z.string().nullable(),
    /**
     * Today's figures, when any month shown is frozen: what was reviewed and what
     * it looks like now are both fair questions, and the gap is why a number moved.
     */
    live: PortfolioFiguresSchema.nullable(),
});
export const PortfolioResponseSchema = z.strictObject({
    period: PortfolioPeriodSchema,
    /** The period's months, oldest first. */
    months: z.array(MonthSchema),
    parks: z.array(PortfolioParkSchema),
    /** Every park shown, added together; null when none has figures. */
    total: PortfolioFiguresSchema.nullable(),
    /**
     * The budgets of the parks that have one, added together; null when none does.
     * Parks only: an entity's income is largely the parks' management fees.
     */
    budget: PortfolioBudgetSchema.nullable(),
    /** How many parks `budget` covers, so a partial total says so. */
    budgetedParks: z.number().int().nonnegative(),
    /** The parks' actuals added together, parks only; null when none has any. */
    actual: PortfolioBudgetSchema.nullable(),
    /** How many parks `actual` covers. */
    actualParks: z.number().int().nonnegative(),
});
/**
 * The T-12 comparison (Stephanie Friedl, voice memo 2026-10-02): one park's
 * fiscal year, closed months at actual and the rest at budget, with the
 * projected year end against budget and against underwriting.
 */
export const T12QuerySchema = z.strictObject({
    parkId: IdSchema,
    /** The calendar year the fiscal year starts in. Defaults to the one under way. */
    fiscalYear: z.coerce.number().int().min(2020).max(2100).optional(),
});
const T12ProjectionSchema = z.strictObject({
    /** Actuals for closed months plus budget for the rest. */
    projected: z.number(),
    budget: z.number(),
});
export const T12RowSchema = z.strictObject({
    key: z.string(),
    label: z.string(),
    /** The category's GL code; null on a total. */
    code: z.string().nullable(),
    kind: z.enum(['category', 'total']),
    /** 'result' is NOI and net income. */
    section: z.enum(['income', 'expense', 'non_operating', 'result']),
    months: z.array(z.strictObject({
        month: MonthSchema,
        /** Null until the month has closed. */
        actual: z.number().nullable(),
        budget: z.number(),
    })),
    /** Closed months, actual against budget; null before any has closed. */
    toDate: z.strictObject({ actual: z.number(), budget: z.number() }).nullable(),
    /** Through December, when the fiscal year ends elsewhere: her "end 2026" column. */
    calendarYearEnd: T12ProjectionSchema.nullable(),
    yearEnd: T12ProjectionSchema,
    /** The acquisition underwriting's annual figure, where it has one. */
    underwriting: z.number().nullable(),
});
export const T12ResponseSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    kind: ParkKindSchema,
    fiscalYear: z.number().int(),
    /** The twelve months, oldest first. */
    months: z.array(MonthSchema),
    /** The last month with actuals loaded; null before any. */
    actualThrough: MonthSchema.nullable(),
    calendarYearEndMonth: MonthSchema.nullable(),
    rows: z.array(T12RowSchema),
});
/**
 * The manager budget view, SOW deliverable 7: budget against actual on the GL
 * lines an administrator chose, for the member's own parks.
 *
 * Stephanie Friedl, 2026-10-22 (Forge #1113): "a budget and highlight which items
 * on there directly from Rent Manager that I want them to see, like expense
 * accounts ... similar to the portfolio dashboard up at the top, how they can view
 * month by month, and then quarter, and then year ... compare year over year".
 */
/** A month, `2026-08`; a quarter, `2026-Q3`; or a calendar year, `2026`. */
export const BudgetViewPeriodSchema = z
    .string()
    .regex(/^\d{4}(-((0[1-9]|1[0-2])|Q[1-4]))?$/, 'Expected YYYY, YYYY-MM or YYYY-Qn');
export const ManagerBudgetQuerySchema = z.strictObject({ period: BudgetViewPeriodSchema });
/**
 * A park's month as its manager closes it, for its moves or its budget
 * (Stephanie Friedl, 2026-10-23). A manager cannot reopen what they closed; an
 * administrator can. Display names only.
 */
export const ManagerMonthStateSchema = z.strictObject({
    closed: z.boolean(),
    closedAt: TimestampSchema.nullable(),
    closedBy: z.string().nullable(),
    /** The last reopen, kept after the month is closed again. */
    reopenedAt: TimestampSchema.nullable(),
    reopenedBy: z.string().nullable(),
});
/** Close or reopen one park's month. */
export const ManagerMonthActionRequestSchema = z.strictObject({
    parkId: IdSchema,
    month: MonthSchema,
});
export const ManagerMonthActionResponseSchema = ManagerMonthActionRequestSchema;
/** A manager's note on a line of the budget view for one month. */
export const ManagerBudgetNoteSchema = z.strictObject({
    body: z.string(),
    author: z.string().nullable(),
    updatedAt: TimestampSchema,
});
/** Writes a line's note for the month; an empty body removes it. */
export const SetManagerBudgetNoteRequestSchema = z.strictObject({
    parkId: IdSchema,
    month: MonthSchema,
    kind: z.enum(['line', 'total']),
    glCode: z.string().min(1).max(20),
    body: z.string().trim().max(2000),
});
/**
 * A line against its annual goal, the fiscal year's budget, judged on the year to
 * date the way the portfolio report judges revenue and NOI. Stephanie Friedl,
 * 2026-10-23 (Spark 287224): "apply the colors for if they're on track for the
 * annual goal for each item". Cents.
 */
export const ManagerBudgetYearSchema = z.strictObject({
    /** The fiscal year's whole budget: the annual goal. */
    yearBudget: z.number(),
    /** Budget from the fiscal year's first month through the park's `through`. */
    budget: z.number(),
    actual: z.number(),
    /** Actual as a share of the budget to date, rounded. Null when nothing was budgeted to date. */
    percentOfBudget: z.number().int().nullable(),
    /**
     * What the colour bands judge: 100 is exactly on budget and higher is better.
     * The same as `percentOfBudget` where more is better, income; mirrored about
     * 100 where less is, expenses and contra-income such as concessions, so 12%
     * over an expense budget is 88, the same verdict as 12% short on income.
     */
    pacePercent: z.number().int().nullable(),
});
/** Cents throughout. */
export const ManagerBudgetLineSchema = z.strictObject({
    /**
     * A GL line, or the TOTAL Rent Manager prints under a category: every line in
     * the category, shown to managers or not, so it matches Rent Manager's own
     * total. A total's glCode is its category's.
     */
    kind: z.enum(['line', 'total']),
    section: z.enum(['income', 'expense', 'non_operating']),
    categoryCode: z.string(),
    categoryName: z.string(),
    glCode: z.string(),
    glName: z.string(),
    /** The whole period's budget. */
    periodBudget: z.number(),
    /** Budget over the months compared: the closed ones, or the whole period before any close. */
    budget: z.number(),
    /** Actual over the months compared; null before any month of the period has closed. */
    actual: z.number().nullable(),
    /** The same months a year earlier; null when that year has no actuals loaded. */
    lastYear: z.number().nullable(),
    /**
     * On a total: the category has lines with money in this period that managers
     * are not shown, so the lines shown will not add up to it. Always false on a line.
     */
    unshownLines: z.boolean(),
    /** Null before any month of the period has closed, or while a month of the year so far has no actuals. */
    yearToDate: ManagerBudgetYearSchema.nullable(),
    /**
     * Off the month's own budget by more than the green bands allow, either way,
     * or money on a line with none budgeted, and by $100 or more: what the manager
     * is asked to explain once the month is closed. False on a longer period.
     */
    irregular: z.boolean(),
    /** The manager's note on the line for the month; null when none, or on a longer period. */
    note: ManagerBudgetNoteSchema.nullable(),
});
export const ManagerBudgetParkSchema = z.strictObject({
    parkId: IdSchema,
    parkName: z.string(),
    /** The last month compared: the period's last closed month, or null before any closed. */
    through: MonthSchema.nullable(),
    /** The fiscal year's first month, where `yearToDate` starts; null when no line has one. */
    yearFrom: MonthSchema.nullable(),
    /**
     * On a one-month period: whether the month is closed in Month close
     * (`reconciled`), which is when notes open, and the manager's own close of
     * their budget month. Null on a longer period.
     */
    budgetClose: z
        .strictObject({ reconciled: z.boolean(), state: ManagerMonthStateSchema })
        .nullable(),
    lines: z.array(ManagerBudgetLineSchema),
});
export const ManagerBudgetResponseSchema = z.strictObject({
    period: BudgetViewPeriodSchema,
    /** The period's months, oldest first. */
    months: z.array(MonthSchema),
    /** Lines and totals an administrator has chosen. Zero means a manager sees nothing yet. */
    chosenLines: z.number().int().nonnegative(),
    /** The caller may write notes and close a budget month (`months.close`). */
    canNote: z.boolean(),
    /** The caller may reopen a closed budget month (`reports.reconcile`). */
    canReopen: z.boolean(),
    parks: z.array(ManagerBudgetParkSchema),
});
// ---------------------------------------------------------------------------
// Deliverable 8 — report notes
// ---------------------------------------------------------------------------
/**
 * The line on the portfolio report a note is about.
 *
 * Stephanie Friedl, screen recording 2026-10-20, 11:23: "I would really like if
 * I can have notes in here with each item" -- her permanent notes on each line
 * before the report goes out, "because they can see the colour around it and
 * they can see what questions they still have." A note with no line item is one
 * of the notes at the bottom, which is where the meeting's own notes go.
 */
export const ReportLineItemSchema = z.enum([
    'revenue',
    'operating_expense',
    'noi',
    'net_income',
    'collections',
    'occupancy',
    'gross_move_ins',
    'net_move_ins',
    'full_time_residents',
]);
export const ReportNoteSchema = z.strictObject({
    id: IdSchema,
    /** Null means the note is about the whole estate rather than one park. */
    parkId: IdSchema.nullable(),
    parkName: z.string().nullable(),
    month: MonthSchema,
    /** Null for a note about the park's month as a whole. */
    lineItem: ReportLineItemSchema.nullable(),
    body: z.string(),
    /** Display name of the author, or null if their membership was removed. */
    author: z.string().nullable(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const ReportNoteQuerySchema = z.strictObject({
    from: MonthSchema,
    to: MonthSchema,
    parkId: ParkIdFilterSchema,
});
export const ReportNoteListResponseSchema = z.strictObject({
    from: MonthSchema,
    to: MonthSchema,
    items: z.array(ReportNoteSchema),
});
export const CreateReportNoteRequestSchema = z.strictObject({
    /** Omit for an estate-wide note. */
    parkId: IdSchema.nullable().default(null),
    month: MonthSchema,
    /** Omit for a note about the month as a whole. */
    lineItem: ReportLineItemSchema.nullable().default(null),
    /**
     * Trimmed and non-empty. The database enforces the same thing, because an
     * empty note renders as a blank comment nobody can account for.
     */
    body: z.string().trim().min(1).max(4000),
});
// ---------------------------------------------------------------------------
// Deliverable 8 — saved reports
// ---------------------------------------------------------------------------
/** An allowlist of portal views, never a stored path. */
export const SavedReportViewSchema = z.enum(['overview', 'reports']);
/**
 * Who a saved view reaches.
 *
 * Stephanie Friedl on 2026-09-14 asked for a view she can put on a manager's
 * dashboard that they cannot take off: "You can pull your own, and if you mess
 * up the filters or look at it wrong, that's on you, but I've created the one
 * I'm holding you to." A boolean could only say mine or everyone's.
 *
 * `targeted` means the named roles and people, and nobody else.
 */
export const SavedReportAudienceSchema = z.enum(['private', 'organization', 'targeted']);
/**
 * A URL query string with no leading "?".
 *
 * Rejects anything that could turn a saved report's link into a different
 * destination: a scheme, a path separator, a second query, a fragment. The same
 * rule is a CHECK constraint in the database, because a contract protects the
 * API and a constraint protects everything else that can write a row.
 */
export const SavedReportQuerySchema = z
    .string()
    .max(500)
    .refine((value) => !/[?#/\\]/.test(value), 'Must not contain ? # / or \\')
    .refine((value) => !/^[a-z][a-z0-9+.-]*:/i.test(value), 'Must not contain a URL scheme');
export const SavedReportSchema = z.strictObject({
    id: IdSchema,
    name: z.string(),
    view: SavedReportViewSchema,
    query: z.string(),
    audience: SavedReportAudienceSchema,
    /** Roles this view was assigned to. Empty unless `audience` is `targeted`. */
    targetRoleIds: z.array(IdSchema),
    /** People this view was assigned to. Empty unless `audience` is `targeted`. */
    targetMembershipIds: z.array(IdSchema),
    /**
     * True when this view reached the caller because someone assigned it, rather
     * than because they saved it. The UI uses this to say so and to leave out a
     * remove control the API would refuse anyway.
     */
    assigned: z.boolean(),
    /** True when the caller saved it. Only the owner may change or delete it. */
    owned: z.boolean(),
    /** Display name of whoever saved it. Never an email. */
    owner: z.string().nullable(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
/**
 * Re-saving a view you already own, in place.
 *
 * Only the query moves: a quick save is "this is what the view means now", not a
 * chance to silently rename it or change who it reaches. Those stay deliberate
 * acts in the dialog.
 */
export const UpdateSavedReportRequestSchema = z.strictObject({
    query: SavedReportQuerySchema,
});
export const SavedReportListResponseSchema = z.strictObject({
    items: z.array(SavedReportSchema),
});
export const CreateSavedReportRequestSchema = z
    .strictObject({
    name: z.string().trim().min(1).max(120),
    view: SavedReportViewSchema,
    query: SavedReportQuerySchema.default(''),
    audience: SavedReportAudienceSchema.default('private'),
    targetRoleIds: z.array(IdSchema).max(50).default([]),
    targetMembershipIds: z.array(IdSchema).max(200).default([]),
})
    // A targeted view with nobody targeted reaches only its author, which is a
    // private view wearing a misleading label. Rejecting it here means the list
    // never has to explain why an "assigned" view appeared for no one.
    .refine((value) => value.audience !== 'targeted' ||
    value.targetRoleIds.length + value.targetMembershipIds.length > 0, { message: 'Name at least one role or person', path: ['targetRoleIds'] })
    // Targets on a private or organization-wide view would be silently ignored,
    // and a setting that is ignored rather than refused is how a view ends up not
    // reaching the person it named.
    .refine((value) => value.audience === 'targeted' ||
    value.targetRoleIds.length + value.targetMembershipIds.length === 0, { message: 'Only a targeted view can name roles or people', path: ['audience'] });
// ---------------------------------------------------------------------------
// Feed health — shared by the portal strip and the alerting job
// ---------------------------------------------------------------------------
export const FeedKeySchema = z.enum(['rent_manager', 'campspot', 'wordpress']);
/**
 * `unscheduled` is deliberately not `late`.
 *
 * Campspot has a working receiver and no sender: its schedule lives inside
 * Campspot Analytics and cannot be configured from here. Reporting that as
 * "late" would read as a fault in the portal, when the truth is that nobody has
 * turned the tap on yet. `never` means the feed has produced nothing at all.
 */
export const FeedStatusSchema = z.enum(['ok', 'late', 'unscheduled', 'never']);
export const FeedHealthSchema = z.strictObject({
    key: FeedKeySchema,
    label: z.string(),
    /** What the feed writes, in the client's terms rather than table names. */
    describes: z.string(),
    lastUpdatedAt: TimestampSchema.nullable(),
    /** Null when no schedule is configured for this feed. */
    expectedIntervalMinutes: z.number().int().positive().nullable(),
    status: FeedStatusSchema,
});
export const FeedHealthResponseSchema = z.strictObject({
    checkedAt: TimestampSchema,
    feeds: z.array(FeedHealthSchema),
});
// ---------------------------------------------------------------------------
// Deliverable 7 — which budget lines a manager may see
// ---------------------------------------------------------------------------
/**
 * One GL account an administrator can show to managers. Listed from the budget
 * and the Rent Manager P&L since 2026-10-22, rather than from posted charges:
 * charges are what tenants are billed, so expense accounts never appeared, and
 * expense accounts are what Stephanie Friedl wants managers to see.
 */
export const BudgetLineSchema = z.strictObject({
    glAccountId: z.string(),
    /**
     * The account's own line, or the TOTAL Rent Manager prints under the category
     * the account heads (Stephanie Friedl, 2026-10-23). A category account can be
     * both: its own line is the "Other" postings made straight to it.
     */
    kind: z.enum(['line', 'total']),
    name: z.string(),
    /** The account number the budget and the P&L print, such as 5201. */
    code: z.string().nullable(),
    /** Where it sits on the P&L. Null for an account on neither the budget nor the P&L. */
    section: z.enum(['income', 'expense', 'non_operating']).nullable(),
    /** Its P&L category's account number, such as 5100. */
    categoryCode: z.string().nullable(),
    /** Its P&L category, such as REPAIRS & MAINTENANCE. */
    categoryName: z.string().nullable(),
    /** Rent Manager's own type: Income, Expense, Bank and so on. */
    accountType: z.string().nullable(),
    /** How many posted charges reference this account. */
    chargeCount: z.number().int().nonnegative(),
    managerVisible: z.boolean(),
});
export const BudgetLineListResponseSchema = z.strictObject({
    items: z.array(BudgetLineSchema),
    /** Accounts on neither the budget nor the P&L, omitted from `items` but counted here. */
    unusedAccounts: z.number().int().nonnegative(),
});
export const SetBudgetLineVisibilityRequestSchema = z.strictObject({
    glAccountId: z.string().min(1).max(50),
    /** Which of the account's two rows: its line, or its category's total. */
    kind: z.enum(['line', 'total']).default('line'),
    managerVisible: z.boolean(),
});
